import React from 'react';
import { Card, CardContent, Typography, Box, CardMedia } from '@mui/material';
import { motion } from 'framer-motion';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

const ACCENT = '#2563EB';

const projects = [
  {
    title: 'MLArtifactFS',
    description: 'MLArtifactFS enables you to decouple ML model artifacts from Docker images, allowing instant model version switching without image rebuilds. Read more about the project <a href="https://github.com/jakeBringetto/MLArtifactFS/blob/main/README.md" target="_blank" rel="noopener noreferrer">here.</a>',
    imageUrl: 'docker.png',
    span: 2,
  },
  {
    title: 'Attune',
    description: 'Audio-reactive visual engine. Author particle-based visuals in a small declarative DSL that reacts to live audio in real time. Read more <a href="https://github.com/jakeBringetto/Attune/blob/main/README.md" target="_blank" rel="noopener noreferrer">here.</a>',
    imageUrl: 'wave.jpeg',
    span: 1,
  },
  {
    title: 'Git Lite',
    description: 'Java application with the functionality of the Git version control system. Uses efficient data structures, SHA-1 hashing, and graph traversal for file persistence, branching, checkouts, merging, and remote repositories. Contact me to view the source code.',
    imageUrl: 'java.jpeg',
    span: 1,
  },
  {
    title: 'Matricks',
    description: 'Python library for efficient linear algebra operations. Uses cache optimization and thread/data-level parallelism to achieve 2000x speed-ups via a Python-C interface. Contact me to view the source code.',
    imageUrl: 'cpython.jpeg',
    span: 1,
  },
  {
    title: 'PintOS',
    description: 'Simple operating system supporting user programs, system calls, threads with priority scheduling and donation, and a fast file system with expansible files, buffer cache, and subdirectories. Contact me to view the source code.',
    imageUrl: 'peng.jpeg',
    span: 1,
  },
  {
    title: 'Spotify Bot',
    description: 'Spotify bot for syncing playlists, extracting song features for ML datasets, and predicting playlist membership for unadded songs using a decision tree model. Read more <a href="https://github.com/jakeBringetto/spotify-sync-type-beat/blob/main/README.md" target="_blank" rel="noopener noreferrer">here.</a>',
    videoUrl: 'spotify.mov',
    span: 2,
  },
  {
    title: 'Fire Bot',
    description: 'Specialized chatbot for fire department policies and procedures. Uses RAG via a Pinecone vector database to fetch context for GPT-4, displayed with a Python + Flask + React UI. Read more <a href="https://github.com/jakeBringetto/fire-bot/blob/main/README.md" target="_blank" rel="noopener noreferrer">here.</a>',
    videoUrl: 'firebot.mov',
    span: 1,
  },
  {
    title: 'Vision Transformer for Ed',
    description: 'Assignment teaching vision transformers and compute optimization. Walks through multiple papers, a ViT implemented in JAX, and analysis of a linear-angular attention function.',
    pdfUrl: 'vision.pdf',
    span: 1,
  },
];

const linkSx = {
  '& a': { color: ACCENT, '&:hover': { color: '#1D4ED8' } },
};

function Projects() {
  return (
    <section id="projects" style={{ paddingTop: '80px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h2" gutterBottom>
          Projects
        </Typography>
      </motion.div>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3,
      }}>
        {projects.map((project, index) => (
          <Box
            key={index}
            sx={{ gridColumn: { xs: 'span 1', md: `span ${project.span}` } }}
          >
            <motion.div
              style={{ height: '100%' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {project.videoUrl ? (
                  <CardMedia
                    component="video"
                    controls
                    src={project.videoUrl}
                    title={project.title}
                    sx={{ height: project.span === 2 ? 280 : 180 }}
                  />
                ) : project.pdfUrl ? (
                  <Box sx={{ height: project.span === 2 ? 280 : 180, position: 'relative' }}>
                    <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                      <Viewer fileUrl={project.pdfUrl} />
                    </Worker>
                    <a href={project.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10 }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    src={project.imageUrl}
                    alt={project.title}
                    sx={{ height: project.span === 2 ? 280 : 180, objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {project.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                    sx={linkSx}
                  />
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        ))}
      </Box>
    </section>
  );
}

export default Projects;
